import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { verifyPasswordForRoute, getRequiredRoleForRoute } from '../config/auth';

const { FiX, FiLock, FiShield, FiDollarSign } = FiIcons;

const PasswordModal = ({ isOpen, onClose, targetRoute = '', onSuccess }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const requiredRole = getRequiredRoleForRoute(targetRoute);
  const isAdminSection = requiredRole === 'admin';

  const handleSubmit = (e) => {
    e.preventDefault();
    const result = verifyPasswordForRoute(password, targetRoute);

    if (result.success) {
      sessionStorage.setItem('tap2order_auth', 'true');
      sessionStorage.setItem('tap2order_auth_role', result.role);

      setPassword('');
      setError('');

      if (onSuccess) {
        onSuccess(result.role);
      } else {
        navigate(targetRoute || '/');
        if (onClose) onClose();
      }
    } else {
      setError(result.error || 'Password non corretta');
    }
  };

  const handleClose = () => {
    if (onClose) onClose();
    setPassword('');
    setError('');
  };

  return (
    <AnimatePresence>
      {(isOpen === undefined || isOpen) && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
        >
          <motion.div
            initial={{ scale: 0.92, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.92, opacity: 0 }}
            className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl border border-gray-100"
          >
            <div className="flex items-center justify-between mb-5 pb-3 border-b border-gray-100">
              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  isAdminSection ? 'bg-amber-100 text-amber-700' : 'bg-[#5C715E]/15 text-[#5C715E]'
                }`}>
                  <SafeIcon icon={isAdminSection ? FiDollarSign : FiLock} className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900 leading-tight">
                    {isAdminSection ? 'Accesso Amministrazione & Cassa' : 'Accesso Staff Sala & Cucina'}
                  </h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Area protetta riservata al personale autorizzato
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Chiudi"
              >
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                  Password di Accesso
                </label>
                <div className="relative">
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#5C715E] focus:border-transparent text-gray-900 font-mono text-base"
                    autoFocus
                  />
                  <div className="absolute right-3 top-3.5 text-gray-400">
                    <SafeIcon icon={FiShield} className="w-5 h-5" />
                  </div>
                </div>
                {error && (
                  <motion.p
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-red-600 text-xs font-medium mt-2 bg-red-50 p-2.5 rounded-lg border border-red-200 leading-relaxed"
                  >
                    ⚠️ {error}
                  </motion.p>
                )}
              </div>

              <div className="flex space-x-3 pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2.5 bg-[#5C715E] hover:bg-[#4B5C4D] text-white rounded-xl text-sm font-bold shadow-sm transition-colors"
                >
                  Accedi
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PasswordModal;